/**
 * TabBaseDatos — Pestaña "Base de Datos Fija" del cliente.
 * 
 * Muestra y edita: credenciales, plan de marketing, acuerdos, objetivos anuales.
 */

import { useState } from 'react';
import { Save, Eye, EyeOff, Shield, HardDrive, ExternalLink, Edit2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { Client } from '@/types';
import { ClientModal } from '@/components/clients/ClientModal';

interface Props {
  client: Client;
  onUpdate: (client: Client) => void;
}

export function TabBaseDatos({ client, onUpdate }: Props) {
  const [editing, setEditing] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [form, setForm] = useState({
    marketing_plan: client.marketing_plan || '',
    agreements: client.agreements || '',
    annual_objectives: client.annual_objectives || '',
    credentials: client.credentials || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateClient(client.id, { ...client, ...form });
      onUpdate({ ...client, ...form });
      setEditing(false);
    } catch (error) {
      console.error('Error guardando:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* ─── Información de Contacto ─── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">Información de Contacto</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditModalOpen(true)}
            className="h-7 text-xs gap-1 text-muted-foreground hover:text-primary"
          >
            <Edit2 size={12} />
            Editar
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <InfoRow label="Email" value={client.email} />
          <InfoRow label="Teléfono" value={client.phone} />
          <InfoRow label="Instagram" value={client.instagram} />
          <InfoRow label="Facebook" value={client.facebook} />
          <InfoRow label="TikTok" value={client.tiktok} />
          <InfoRow label="Web" value={client.website} />
          <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-100">
            <span className="text-muted-foreground flex items-center gap-1.5">
              <HardDrive size={14} className="text-blue-500" />
              Carpeta Drive
            </span>
            {client.google_drive_folder_id ? (
              <a
                href={`https://drive.google.com/drive/folders/${client.google_drive_folder_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline flex items-center gap-1 text-xs font-medium"
              >
                Abrir en Drive <ExternalLink size={11} />
              </a>
            ) : (
              <span className="text-xs text-muted-foreground italic">No vinculada</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ─── Credenciales ─── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield size={16} />
            Credenciales
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto h-7 w-7"
              onClick={() => setShowCredentials(!showCredentials)}
            >
              {showCredentials ? <EyeOff size={14} /> : <Eye size={14} />}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {showCredentials ? (
            <textarea
              value={form.credentials}
              onChange={(e) => setForm({ ...form, credentials: e.target.value })}
              className="w-full h-32 rounded-md border bg-background px-3 py-2 text-sm font-mono"
              placeholder="Accesos a redes sociales, herramientas, etc."
            />
          ) : (
            <p className="text-sm text-muted-foreground italic">
              Credenciales ocultas. Haz click en el ojo para ver.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ─── Plan de Marketing ─── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Plan de Marketing</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            value={form.marketing_plan}
            onChange={(e) => setForm({ ...form, marketing_plan: e.target.value })}
            className="w-full h-40 rounded-md border bg-background px-3 py-2 text-sm"
            placeholder="Describe el plan de marketing del cliente..."
            readOnly={!editing}
          />
        </CardContent>
      </Card>

      {/* ─── Acuerdos ─── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Acuerdos</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            value={form.agreements}
            onChange={(e) => setForm({ ...form, agreements: e.target.value })}
            className="w-full h-40 rounded-md border bg-background px-3 py-2 text-sm"
            placeholder="Acuerdos contractuales y condiciones..."
            readOnly={!editing}
          />
        </CardContent>
      </Card>

      {/* ─── Objetivos Anuales ─── */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-base">Objetivos Anuales</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            value={form.annual_objectives}
            onChange={(e) => setForm({ ...form, annual_objectives: e.target.value })}
            className="w-full h-32 rounded-md border bg-background px-3 py-2 text-sm"
            placeholder="Objetivos del año para este cliente..."
            readOnly={!editing}
          />
        </CardContent>
      </Card>

      {/* ─── Actions ─── */}
      <div className="lg:col-span-2 flex justify-end gap-2">
        {editing ? (
          <>
            <Button variant="outline" onClick={() => setEditing(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              <Save size={16} />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </>
        ) : (
          <Button variant="outline" onClick={() => setEditing(true)}>
            Editar Información
          </Button>
        )}
      </div>

      {/* Modal Editar Contacto y Redes */}
      <ClientModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        initialClient={client}
        onSuccess={onUpdate}
      />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value || '—'}</span>
    </div>
  );
}
